import React from 'react';
import { ClipboardText, Target, Stamp, ChartLineUp, Users, ShieldWarning } from '@phosphor-icons/react';

export interface PendingTask {
    taskName: string;
    taskType: 'Review' | 'Goal' | 'Approval' | 'Survey' | 'Calibration' | 'Succession';
    status: 'Pending' | 'In Progress' | 'Completed' | 'Draft' | string;
    flowboardId?: string | null;
    goalId?: string | null;
    cycleId?: string;
    cycleType?: 'Standard' | 'Annual' | 'Onboard' | '360' | 'fiscal' | 'custom' | string;
    reviewPhase?: 'Self Review' | 'Manager Review' | 'Completed' | string;
    phaseLabel?: string;
    employeeName?: string | null;
    managerName?: string | null;
    actionUrl?: string;
    // Goal-specific fields
    goalCycleName?: string;
    goalProgress?: number;
    goalMetrics?: string;
    goalFramework?: 'standard' | 'okr' | 'kpi' | 'mbo' | 'smart' | string;
    sessionId?: string;
    // snake_case variants for backwards compat 
    task_name?: string;
    task_type?: string;
    flowboard_id?: string | null;
    goal_id?: string | null;
    cycle_id?: string;
    cycle_type?: string;
    review_phase?: string;
    phase_label?: string;
    employee_name?: string | null;
    manager_name?: string | null;
    action_url?: string;
    goal_cycle_name?: string;
    goal_progress?: number;
    goal_metrics?: string;
    goal_framework?: string;
    session_id?: string;
}

interface PendingTasksCardProps {
    data: PendingTask[] | { data: PendingTask[]; metadata?: any; meta?: any; schema?: any } | { payload: PendingTask[]; count?: number };
    options?: {
        show_phase?: boolean;
        show_cycle_type?: boolean;
        max_items?: number;
    };
    /** Callback when a task is clicked. If not provided, navigates to review cloud app */
    onTaskClick?: (task: PendingTask) => void;
}

const TASK_TYPE_CONFIG: Record<string, { color: string; icon: typeof ClipboardText }> = {
    'review': { color: 'bg-violet-100 text-violet-700', icon: ClipboardText },
    'goal': { color: 'bg-cyan-100 text-cyan-700', icon: Target },
    'approval': { color: 'bg-purple-100 text-purple-700', icon: Stamp },
    'survey': { color: 'bg-amber-100 text-amber-700', icon: ChartLineUp },
    'calibration': { color: 'bg-indigo-100 text-indigo-700', icon: Users },
    'succession': { color: 'bg-rose-100 text-rose-700', icon: ShieldWarning },
};

const PHASE_LABEL_CONFIG: Record<string, string> = {
    'hr approval': 'bg-purple-100 text-purple-700 border-purple-200',
    'manager review': 'bg-blue-100 text-blue-700 border-blue-200',
    'self review': 'bg-teal-100 text-teal-700 border-teal-200',
    'pending': 'bg-amber-100 text-amber-700 border-amber-200',
    'nomination pending': 'bg-amber-100 text-amber-700 border-amber-200',
    'pip flag': 'bg-red-100 text-red-700 border-red-200',
    'retention': 'bg-orange-100 text-orange-700 border-orange-200',
    'check-in': 'bg-blue-100 text-blue-700 border-blue-200',
    'urgent check-in': 'bg-red-100 text-red-700 border-red-200',
    'escalat': 'bg-purple-100 text-purple-700 border-purple-200',
    'coaching': 'bg-teal-100 text-teal-700 border-teal-200',
};

const getPhaseLabelConfig = (label: string): string => {
    const l = (label || '').toLowerCase();
    for (const [key, color] of Object.entries(PHASE_LABEL_CONFIG)) {
        if (l.includes(key)) return color;
    }
    return 'bg-gray-100 text-gray-600 border-gray-200';
};

const getTaskTypeConfig = (type: string) => {
    const t = (type || '').toLowerCase();

    if (t.includes('goal')) {
        return TASK_TYPE_CONFIG['goal'];
    }
    if (t.includes('survey')) {
        return TASK_TYPE_CONFIG['survey'];
    }
    if (t.includes('calibration')) {
        return TASK_TYPE_CONFIG['calibration'];
    }
    if (t.includes('approval')) {
        return TASK_TYPE_CONFIG['approval'];
    }
    if (t.includes('succession')) {
        return TASK_TYPE_CONFIG['succession'];
    }
    return TASK_TYPE_CONFIG['review'];
};

// Helper function to format goal metrics from JSON string
const formatGoalMetrics = (metricsJson?: string): string | null => {
    if (!metricsJson) return null;
    try {
        const metrics = JSON.parse(metricsJson);

        // Handle currency
        if (metrics.currency_code || metrics.currency) {
            const currency = metrics.currency_code || metrics.currency;
            const target = metrics.target_value || metrics.target;
            if (target) {
                return `${currency} ${Number(target).toLocaleString()}`;
            }
        }

        // Handle percentage
        if (metrics.unit === '%' || metrics.unit === 'percentage') {
            const target = metrics.target_value || metrics.target;
            return target ? `${target}%` : null;
        }

        // Handle other units
        if (metrics.unit && metrics.unit !== 'Unknown') {
            const target = metrics.target_value || metrics.target;
            return target ? `${target} ${metrics.unit}` : null;
        }

        return null;
    } catch {
        return null;
    }
};

// Helper function to get framework badge text
const getFrameworkLabel = (framework?: string): string => {
    if (!framework) return '';
    const f = framework.toLowerCase();
    if (f === 'okr') return 'OKR';
    if (f === 'kpi') return 'KPI';
    if (f === 'mbo') return 'MBO';
    if (f === 'smart') return 'SMART';
    return 'Standard';
};

export const PendingTasksCard: React.FC<PendingTasksCardProps> = ({ data, options = {}, onTaskClick }) => {
    const {
        max_items
    } = options;

    // Handle task click - navigate to review cloud app
    const handleTaskClick = (task: PendingTask) => {
        if (onTaskClick) {
            onTaskClick(task);
            return;
        }

        // Use actionUrl if available
        const actionUrl = task.actionUrl || task.action_url;
        if (actionUrl) {
            window.location.href = actionUrl;
            return;
        }

        // Fallback to existing logic
        const cycleId = task.cycleId || task.cycle_id;
        const goalId = task.goalId || task.goal_id;
        const taskType = (task.taskType || task.task_type || '').toLowerCase();

        if (taskType.includes('review') && cycleId) {
            window.location.href = `/performspark/performance/reviews?cycleId=${cycleId}`;
        } else if (taskType.includes('goal') && goalId) {
            window.location.href = `/performspark/goals/${goalId}`;
        }
    };

    // Handle various data wrapper formats from the API
    let items: PendingTask[] = [];
    if (Array.isArray(data)) {
        items = data;
    } else if (data && typeof data === 'object') {
        if ('data' in data && Array.isArray(data.data)) {
            items = data.data;
        } else if ('payload' in data && Array.isArray(data.payload)) {
            items = data.payload;
        }
    }

    const displayItems = max_items ? items.slice(0, max_items) : items;

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <ClipboardText size={28} className="text-gray-400" />
                </div>
                <span className="text-sm font-medium">No pending tasks</span>
                <span className="text-xs text-gray-400 mt-1">Your tasks will appear here</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5 p-3 h-full overflow-auto">
            {displayItems.map((item, index) => {
                // Support both camelCase and snake_case fields
                const itemTaskName = item.taskName || item.task_name || '';
                const itemTaskType = item.taskType || item.task_type || 'Review';
                const itemPhaseLabel = item.phaseLabel || item.phase_label || '';
                const itemFlowboardId = item.flowboardId || item.flowboard_id;
                const itemGoalId = item.goalId || item.goal_id;
                const itemEmployeeName = item.employeeName || item.employee_name || null;
                const itemManagerName = item.managerName || item.manager_name || null;
                const itemGoalCycleName = item.goalCycleName || item.goal_cycle_name || '';
                const itemGoalProgress = item.goalProgress ?? item.goal_progress ?? 0;
                const itemGoalMetrics = item.goalMetrics || item.goal_metrics;
                const itemGoalFramework = item.goalFramework || item.goal_framework;

                const taskTypeConfig = getTaskTypeConfig(itemTaskType);
                const TaskTypeIcon = taskTypeConfig.icon;
                const isGoal = itemTaskType.toLowerCase().includes('goal');
                const isSurvey = itemTaskType.toLowerCase().includes('survey');

                // Create unique key
                const uniqueKey = `task-${itemFlowboardId || itemGoalId || index}-${index}`;

                // Render Goal Card
                if (isGoal) {
                    const formattedMetrics = formatGoalMetrics(itemGoalMetrics);
                    const frameworkLabel = getFrameworkLabel(itemGoalFramework);

                    return (
                        <div
                            key={uniqueKey}
                            className="group bg-white rounded-lg border border-gray-200 p-3 hover:border-cyan-300 hover:shadow-sm transition-all cursor-pointer"
                            onClick={() => handleTaskClick(item)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleTaskClick(item);
                                }
                            }}
                        >
                            {/* Row 1: Task Type Badge + Phase */}
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${taskTypeConfig.color}`}>
                                    <TaskTypeIcon size={10} weight="bold" />
                                    {itemTaskType}
                                </span>
                                {itemPhaseLabel && (
                                    <span className={`flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${getPhaseLabelConfig(itemPhaseLabel)}`}>
                                        {itemPhaseLabel}
                                    </span>
                                )}
                            </div>

                            {/* Row 2: Goal Name */}
                            <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-1.5 group-hover:text-cyan-600 transition-colors">
                                {itemTaskName}
                            </h3>

                            {/* Row 3: Employee + Cycle Name */}
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2.5">
                                {itemEmployeeName && (
                                    <>
                                        <span className="truncate">{itemEmployeeName}</span>
                                        {itemGoalCycleName && <span className="text-gray-300">·</span>}
                                    </>
                                )}
                                {itemGoalCycleName && (
                                    <span className="truncate font-medium">{itemGoalCycleName}</span>
                                )}
                            </div>

                            {/* Row 4: Progress Bar */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-600">Progress</span>
                                    <span className="font-semibold text-blue-600">{itemGoalProgress}%</span>
                                </div>
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min(100, Math.max(0, itemGoalProgress))}%` }}
                                    />
                                </div>
                            </div>

                            {/* Row 5: Metrics + Framework */}
                            {(formattedMetrics || frameworkLabel) && (
                                <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-gray-100">
                                    {formattedMetrics && (
                                        <span className="text-xs text-gray-600 font-medium">
                                            Target: {formattedMetrics}
                                        </span>
                                    )}
                                    {frameworkLabel && (
                                        <span className="ml-auto px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-semibold uppercase">
                                            {frameworkLabel}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                }

                // Render Survey Card
                if (isSurvey) {
                    return (
                        <div
                            key={uniqueKey}
                            className="group bg-white rounded-lg border border-gray-200 p-3 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer"
                            onClick={() => handleTaskClick(item)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleTaskClick(item);
                                }
                            }}
                        >
                            {/* Row 1: Task Type Badge + Phase */}
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${taskTypeConfig.color}`}>
                                    <TaskTypeIcon size={10} weight="bold" />
                                    {itemTaskType}
                                </span>
                                {itemPhaseLabel && (
                                    <span className={`flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${getPhaseLabelConfig(itemPhaseLabel)}`}>
                                        {itemPhaseLabel}
                                    </span>
                                )}
                            </div>

                            {/* Row 2: Survey Name */}
                            <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-1.5 group-hover:text-amber-600 transition-colors">
                                {itemTaskName}
                            </h3>

                            {/* Row 3: Employee + Manager */}
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                {itemEmployeeName && (
                                    <>
                                        <span className="truncate">{itemEmployeeName}</span>
                                        {itemManagerName && <span className="text-gray-300">·</span>}
                                    </>
                                )}
                                {itemManagerName && (
                                    <span className="truncate">Mgr: {itemManagerName}</span>
                                )}
                            </div>
                        </div>
                    );
                }

                // Render Standard Card (Review, Calibration, Approval)
                return (
                    <div
                        key={uniqueKey}
                        className="group bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                        onClick={() => handleTaskClick(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleTaskClick(item);
                            }
                        }}
                    >
                        {/* Row 1: Task Type Badge + Phase */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${taskTypeConfig.color}`}>
                                <TaskTypeIcon size={10} weight="bold" />
                                {itemTaskType}
                            </span>
                            {itemPhaseLabel && (
                                <span className={`flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${getPhaseLabelConfig(itemPhaseLabel)}`}>
                                    {itemPhaseLabel}
                                </span>
                            )}
                        </div>

                        {/* Row 2: Employee Name */}
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                                {itemEmployeeName || 'Unknown Employee'}
                            </span>
                        </div>

                        {/* Row 3: Task Name + Manager */}
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span className="truncate">{itemTaskName}</span>
                            {itemManagerName && (
                                <>
                                    <span className="text-gray-300">·</span>
                                    <span className="truncate">Mgr: {itemManagerName}</span>
                                </>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
